/**
 * entities/notification/ui/NotificationItem — Localized notification renderer.
 *
 * Maps `type` + `payload` to a localized display string via react-i18next.
 * Falls back to the raw `title` / `body` fields if no i18n key matches.
 *
 * Timestamp formatting is locale-aware: "just now" for <60s, a relative
 * phrase (Intl.RelativeTimeFormat) for <7 days, and a localized absolute
 * date otherwise.
 *
 * When the notification's payload carries `lift_ids` (documents
 * uploaded/updated, lift-scoped alerts), the title/body block becomes a link
 * to that lift's detail page and marks the notification read on click. The
 * mark-read button always stays a sibling of that link — never nested inside
 * it — so the two actions (open lift / mark read) don't conflict.
 *
 * FSD: entities may import from shared only.
 */

import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/shared/lib/cn';
import type { Notification } from '../model/types';
import { getNotificationLiftIds } from '../lib/payload';

interface NotificationItemProps {
  notification: Notification;
  /** Called when the user requests to mark this notification read. */
  onMarkRead?: (id: string) => void;
  /** Whether a mark-read action is currently in-flight for this item. */
  isMarkingRead?: boolean;
  /** Extra CSS class name(s). */
  className?: string;
}

/**
 * Resolves the localized display title for a notification.
 *
 * Tries `notifications:types.<type>.title` first, then falls back to
 * the server-provided `title` field (which is already a human-readable string).
 */
function useNotificationTitle(notification: Notification): string {
  const { t } = useTranslation('notifications');
  const key = `types.${notification.type}.title`;
  const localized = t(key, { defaultValue: '', ...extractPayload(notification.payload) });
  return localized || notification.title;
}

/**
 * Resolves the localized display body for a notification.
 * Falls back to the server-provided `body` field.
 */
function useNotificationBody(notification: Notification): string {
  const { t } = useTranslation('notifications');
  const key = `types.${notification.type}.body`;
  const localized = t(key, { defaultValue: '', ...extractPayload(notification.payload) });
  return localized || notification.body;
}

/**
 * Safely extracts payload values for i18n interpolation.
 * Only scalar string/number values are passed through.
 */
function extractPayload(payload: unknown): Record<string, string | number> {
  if (typeof payload !== 'object' || payload === null) return {};
  const result: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(payload as Record<string, unknown>)) {
    if (typeof v === 'string' || typeof v === 'number') {
      result[k] = v;
    }
  }
  return result;
}

/**
 * Formats a timestamp into a locale-aware relative or absolute date string:
 *   - < 60s:     t('time.justNow')
 *   - < 7 days:  Intl.RelativeTimeFormat (minute/hour/day units, `numeric: 'auto'`
 *                so it reads "yesterday"/"gestern" etc. where applicable)
 *   - otherwise: toLocaleDateString(locale)
 */
function formatTimestamp(isoString: string, locale: string, justNowLabel: string): string {
  try {
    const date = new Date(isoString);
    const now = Date.now();
    const diffMs = now - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60_000);
    const diffHours = Math.floor(diffMs / 3_600_000);
    const diffDays = Math.floor(diffMs / 86_400_000);

    if (diffMinutes < 1) return justNowLabel;

    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    if (diffMinutes < 60) return rtf.format(-diffMinutes, 'minute');
    if (diffHours < 24) return rtf.format(-diffHours, 'hour');
    if (diffDays < 7) return rtf.format(-diffDays, 'day');

    return date.toLocaleDateString(locale);
  } catch {
    return '';
  }
}

export function NotificationItem({
  notification,
  onMarkRead,
  isMarkingRead = false,
  className,
}: NotificationItemProps) {
  const { t, i18n } = useTranslation('notifications');
  const navigate = useNavigate();
  const title = useNotificationTitle(notification);
  const body = useNotificationBody(notification);
  const isUnread = !notification.is_read;
  const liftIds = getNotificationLiftIds(notification.payload);
  const liftId = liftIds[0];
  const liftPath = liftId ? `/lifts/${liftId}` : undefined;

  function handleMarkRead() {
    if (onMarkRead && isUnread) {
      onMarkRead(notification.id);
    }
  }

  function handleLiftLinkClick(e: MouseEvent<HTMLAnchorElement>) {
    if (!liftPath) return;
    // Let modified clicks (new tab, etc.) behave natively.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    if (isUnread && onMarkRead) {
      onMarkRead(notification.id);
    }
    void navigate({ to: liftPath as string });
  }

  const contentBlock = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {isUnread && (
            <span
              aria-label={t('unread')}
              className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary"
            />
          )}
          <span className="font-medium truncate">
            {title}
          </span>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">
          {formatTimestamp(notification.created_at, i18n.language, t('time.justNow'))}
        </span>
      </div>

      {body && (
        <p className="text-muted-foreground line-clamp-2">
          {body}
        </p>
      )}
    </>
  );

  return (
    <div
      role="article"
      aria-label={title}
      data-testid="notification-item"
      data-notification-id={notification.id}
      className={cn(
        'flex flex-col gap-1 rounded-md border bg-card p-3 text-sm shadow-sm transition-all',
        liftPath && 'cursor-pointer hover:border-primary/40 hover:shadow-md',
        isUnread && 'border-l-2 border-l-primary',
        className,
      )}
    >
      {liftPath ? (
        <a
          href={liftPath}
          onClick={handleLiftLinkClick}
          className="flex flex-col gap-1 no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm"
          data-testid="notification-item-lift-link"
        >
          {contentBlock}
        </a>
      ) : (
        contentBlock
      )}

      {isUnread && onMarkRead && (
        <button
          type="button"
          onClick={handleMarkRead}
          disabled={isMarkingRead}
          aria-label={t('markRead')}
          data-testid="mark-read-btn"
          className={cn(
            'ml-4 -my-2 inline-flex min-h-[44px] items-center self-start px-1 text-xs text-primary underline-offset-2 hover:underline',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          {t('markRead')}
        </button>
      )}
    </div>
  );
}
